/**
 * Presentation numbers for the fight renderer (docs/fight.md §11–§13). Rendering only: nothing
 * here changes the simulation, so these can be tuned freely. Values come from the prototype
 * (reference/caged-prototype.html) unless marked (placeholder).
 */

export const CAMERA = {
  /** World box (world units) the camera fits around the octagon centre. */
  fitWidth: 1330,
  fitHeight: 1225,
  /** Landscape: the fight sits below this HUD band (CSS px). */
  hudBand: 62,
  /** Portrait fallback: fit this width with a side margin, in the top share of the screen. */
  portraitFitWidth: 1290,
  portraitMargin: 12,
  portraitHeightShare: 0.56,
  /** When zoomed in, the camera eases toward the fighters at this rate (per second). */
  followRate: 7.7,
  /** When zoomed in, the camera aims this far (× S) above the fighters' midpoint. */
  followLift: 0.4,
  /** Zoom above which the camera follows the fighters. */
  followFromZoom: 1.01,
  /** KO replay: the camera cuts in this close and follows the fighters. */
  replayZoom: 1.5,
  /**
   * Short landscape screens (a phone under its browser's bars) zoom in and follow the fighters,
   * so they're drawn at least this big (CSS px per world unit, about the 844 × 390 fit), up to
   * `maxAutoZoom` (placeholders).
   */
  minScale: 0.24,
  maxAutoZoom: 1.5,
};

export const RENDERER = {
  maxResolution: 2,
  /** Use the 2048 px arena when the screen's longest side × DPR is below this. */
  smallArenaBelow: 2000,
};

/** Per arena: the flat colour beyond the image and the colour of the hit puffs. */
export interface ArenaLook {
  flat: number;
  puff: number;
}

export const ARENA_LOOKS: Record<string, ArenaLook> = {
  snow: { flat: 0xeaf1f6, puff: 0xffffff },
};

/** Fallback for arenas without an entry above (placeholder: dust). */
export const DEFAULT_ARENA_LOOK: ArenaLook = { flat: 0xeaf1f6, puff: 0xe9dfcf };

export const ARENA = {
  /** Top and bottom fade into the flat colour over this many world units. */
  fade: 160,
  /** Mirrored copies of the image on each side (1 is enough up to ~4:1 screens). */
  mirrorCopies: 2,
};

export const COLORS = {
  navy: 0x1e2330,
  burstFill: '#FFF6D6',
  blockRing: 0xbfe5ff,
  star: '#FFD24A',
  shadow: 0x28466e,
  /** Name tag dots follow the HUD: amber for you, muted for the opponent (Fight Page design). */
  tagYou: 0xf5a03c,
  tagOpponent: 0xd9d2c5,
  tagText: 0xf2ede4,
  tagBg: 0x1c1a18,
  /** Floating text tints. */
  damageToYou: 0xffb3b3,
  damageToOpponent: 0xffffff,
  info: 0xcfe9ff,
  gold: 0xffd27a,
  /** "Rocked!" label (D13). */
  rocked: 0xff9f8a,
  white: 0xffffff,
  signatureGhost: 0xffd9a0,
  grabRing: 0xffd27a,
};

export const FIGHTER_LOOK = {
  /** Hit flash: white silhouette alpha = hitFlash × this. */
  flashAlpha: 0.55,
  /** Face swaps to "sting" while the hit flash is above this. */
  stingFlash: 0.3,
  /** The rear fist is shaded like the design (brightness .78) unless it is attacking. */
  rearFistTint: 0xc7c7c7,
  /** Locker effects (M7): the aura's radius in S and its alpha, and a spark's radius in S. */
  lookAuraSize: 1.05,
  lookAuraAlpha: 0.55,
  lookSparkSize: 0.13,
  /** Idle bob: frequency (rad/s) and amplitudes (× S for fists, fraction for the body). */
  bobRate: 7,
  bobFist: 0.02,
  bobBody: 0.03,
  /** Block pose blends in over this long (s of blockHeld). */
  blockBlend: 0.05,
  /** Shadow ellipse (× S) and alpha. */
  shadowRadiusX: 0.45,
  shadowRadiusY: 0.09,
  shadowOffsetX: 0.09,
  shadowAlpha: 0.24,
  /** KO tip-over. */
  koTip: 1.45,
  koTipTime: 0.35,
  /** Dizzy stars: head height (× body draw height), orbit (× S), size (× S). */
  starHead: 0.85,
  starOrbitX: 0.32,
  starOrbitY: 0.1,
  starLift: 0.18,
  starSize: 0.07,
  starSpin: 4,
  /** Stars after a takedown show for this long (s of down time), fading over the last part. */
  starsAfterTakedown: 0.75,
  starsFade: 0.15,
  /** Trail of faded copies (signature dash); the grapple dash leaves a light (white) trail. */
  ghostLife: 0.22,
  ghostAlpha: 1.3,
  ghostGrappleAlpha: 1.6,
  ghostLean: 0.18,
  ghostPool: 18,
  /** Signature wind-up glow. */
  glowRadius: 0.7,
  glowGrow: 0.4,
  glowLift: 0.5,
  glowFade: 0.06,
  /** Follow-through after a takedown slam lasts this long (s of the target's down time). */
  slamFollow: 0.22,
  /** Rocked (D13): the body sways and wobbles under circling stars (placeholder look). */
  rockedSway: 0.1,
  rockedSwayRate: 5.5,
  rockedWobble: 0.035,
  /** Open punish window (D14): a pulsing gold glow on both fists (placeholder look). */
  punishGlowTint: 0xffc24d,
  punishGlowSize: 0.36,
  punishGlowAlpha: 0.75,
  punishGlowRate: 14,
  punishGlowFade: 0.12,
};

/**
 * A Locker pet (M7, docs/fight.md §13): where it flies and how it moves. Lengths × S unless
 * noted; times in seconds; angles in radians (placeholder look, tuned by eye).
 */
export const PET_LOOK = {
  /** Where it hovers: this far behind its fighter (away from the opponent), at this share of the fighter's body height. */
  homeX: 0.8,
  homeY: 0.6,
  /** Further back while its fighter winds up the signature. */
  signatureBack: 0.4,
  /** How fast it follows (spring rate, 1/s): flying, and sitting on the glove. */
  follow: 7,
  followSit: 30,
  /** Lifts over its fighter's head when crossing to the other side: per S of distance, at most `hop`. */
  hopPerS: 0.35,
  hop: 0.6,
  /** Bob while flying. */
  bob: 0.05,
  bobRate: 3.1,
  /** Leans into its flight: radians per S/s of speed, at most `maxTilt`. */
  tilt: 0.05,
  maxTilt: 0.35,
  /** Sits on the rear glove once its fighter has stood still this long (moving slower than `stillSpeed` S/s), on the glove's outer side. */
  sitAfter: 0.7,
  stillSpeed: 0.4,
  sitOut: 0.1,
  /** Wings folded down while sitting. */
  fold: 0.5,
  /** When its fighter is hit: kicked away from the opponent (S/s), flapping faster for a moment. */
  kick: 3.2,
  flinchTime: 0.5,
  flinchFlap: 1.8,
  /** KO: lands on the floor this far in front of the fallen fighter. */
  koX: 0.62,
  /** Win: circles its fighter (rad/s), this wide (× S) at this share of the body height. */
  winSpin: 2.6,
  winRadius: 0.95,
  winHeight: 0.8,
  /** Ghost (no wings): sways as it floats. */
  sway: 0.12,
  swayRate: 1.8,
  /** Its shadow on the floor (radius × S) and the glow's alpha (firefly). */
  shadowSize: 0.15,
  shadowAlpha: 0.2,
  glowAlpha: 0.5,
};

export const FX = {
  shake: { strike: 5, blocked: 2, grapple: 9, signature: 16, ko: 18, punish: 11, rocked: 8, decay: 40 },
  flash: { signature: 0.55, ko: 0.4, punish: 0.22, fade: 0.35 },
  burst: { strike: 1, takedown: 1.3, signature: 1.9, counter: 0.9, punish: 1.6, radius: 0.28, life: 0.2, pool: 6 },
  puffs: { strike: 7, takedown: 12, signature: 20, reversal: 8, pool: 72 },
  ring: { life: 0.25, from: 0.15, grow: 0.35, pool: 4 },
  /** Takedown dust ring on the ground (placeholder look). */
  groundRing: { life: 0.38, from: 0.35, grow: 0.85, squash: 0.36, alpha: 0.9 },
  /** Ring closing around the defender at the grab moment (placeholder look). */
  grabRing: { life: 0.2, from: 0.78, grow: -0.3, lift: 0.55, alpha: 0.6 },
  /** Floating text; damage numbers start `above` × S over the name tag. */
  float: { life: 0.8, rise: 34, damageSize: 22, labelSize: 16, stroke: 5, pool: 8, pop: 0.12, above: 0.3 },
  /**
   * Heavier hits with the attacker's skill (D48): per rank of Power (strikes, signature) or
   * Takedown (slams), the spark and the snow puffs grow by `size` and the shake by `shake`.
   */
  weight: { size: 0.06, shake: 0.04 }, // placeholder
};

/** UI sizes in screen space grow from the phone design (390 px short side) to desktop (810 px). */
export const UI_SCALE = { fromShortSide: 390, toShortSide: 810, max: 14 / 12 };
